import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import api from '../services/api';

interface PayNowButtonProps {
  orderId: string;
  currency: string;
  restaurantId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  onPaymentInitiated?: () => void;
  sx?: SxProps<Theme>;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  amount?: number;
  userId?: string;
  itemsToPay?: {
    orderItemId: string;
  }[];
}

export const PayNowButton: React.FC<PayNowButtonProps> = ({
  orderId,
  currency,
  restaurantId,
  onSuccess,
  onCancel,
  onPaymentInitiated,
  sx,
  loading: externalLoading,
  setLoading: externalSetLoading,
  amount,
  userId,
  itemsToPay
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = externalLoading !== undefined ? externalLoading : internalLoading;
  const setLoading = externalSetLoading || setInternalLoading;
  const [error, setError] = useState<string | null>(null);

  const handlePayNow = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      if (itemsToPay) {
        response = await api.post('/payments/partial', {
          orderId,
          userId: userId || null,
          currency,
          paymentMethod: {
            type: 'credit_card', // This should be dynamic
            provider: 'onvo', // TODO: Make this dynamic based on restaurant configuration
          },
          items: itemsToPay,
          callbackUrl: `${window.location.origin}/payment/callback`,
        });
      } else {
        response = await api.post('/payments/initiate', { 
          orderId, 
          currency, 
          restaurantId,
          callbackUrl: `${window.location.origin}/payment/callback`
        });
      }
      
      if (response.data.checkoutUrl) {
        if (onPaymentInitiated) {
          onPaymentInitiated();
        }
        window.location.href = response.data.checkoutUrl;
      } else if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Payment initiation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={handlePayNow}
      disabled={loading || (!!itemsToPay && (!amount || amount <= 0))}
      fullWidth={false}
      sx={sx}
    >
      {loading ? (
        <CircularProgress size={24} color="inherit" />
      ) : (
        amount ? `Pagar ₡${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : 'Pagar Ahora'
      )}
    </Button>
  );
}; 